import { inject, Injectable } from '@angular/core';

import { AuthEvent } from '../model/auth-event';
import { AuthState } from '../model/auth-state';
import { Command } from '../model/command';
import { describeState } from '../model/transition';
import { AuthorizationErrorSummary } from '../oauth/authorization-request';

import {
  AuthDuration,
  AUTH_TELEMETRY,
  AuthFailureLabel,
  CredentialStoreOperation,
  DurationLabels,
  DurationOutcome,
  failureLabel,
  SessionOrigin,
} from './auth-telemetry';

/**
 * Durations whose start and end are simply "the machine is in this state".
 *
 * Expressed as a predicate over the state, rather than as start/stop calls sprinkled through the
 * transition function, so a new path into `authorizing` is measured the moment it exists.
 */
const REGIONS: readonly { readonly name: AuthDuration; readonly holds: (state: AuthState) => boolean }[] = [
  { name: 'auth.login', holds: state => state.tag === 'authorizing' },
  { name: 'auth.refresh', holds: state => state.tag === 'refreshing' },
];

/**
 * The facets only the state that opens a region knows.
 *
 * Both are read at the open for the same reason: a region does not end in the state it began in. A
 * Grant advances to a Token Exchange before it reaches `authorized`, and a refresh closes into
 * `authorized` or `unauthenticated` — so a close sees a state that holds neither the source nor the
 * trigger. Asking there labelled every *failure* and no success at all: a facet with no denominator
 * under it.
 *
 * Both are now plain field reads because both facts live on the state that carries them for the whole
 * region. The source used to be reconstructed from the Grant's step, which meant a Challenge and both
 * Token Exchanges answered `undefined` and an MFA sign-in went unlabelled; `authorizing.source` closed
 * that. The trigger has never been derivable at all, which is what made the gate on `appResumed`
 * unmeasurable from the vital.
 *
 * A key is present only when it has a value, so the adapter can spread this straight into a context
 * without writing `source: undefined` into a facet.
 */
function labelsOn(entering: AuthState): DurationLabels {
  if (entering.tag === 'authorizing') return { source: entering.source };
  return entering.tag === 'refreshing' ? { trigger: entering.trigger } : {};
}

/**
 * How the session that just became usable was obtained.
 *
 * `refreshing` covers both a cold-start restore and an ordinary refresh, and `stale` is what
 * separates them: there were no tokens to replace at launch. A `refresh` here is also what a
 * Company Switch reports, which is correct — it re-mints the same person's session.
 */
function originOf(before: AuthState): SessionOrigin {
  if (before.tag === 'refreshing') return before.stale === null ? 'restore' : 'refresh';
  return 'signIn';
}

/**
 * Whether reaching `unauthenticated` ended a session that existed, which is what `auth.logout`
 * claims and what `sessionEstablished` is the denominator for.
 *
 * Arriving on the sign-in screen is not the question. Most ways of arriving there end nothing: a
 * launch that reads an empty Vault, a launch on a device that cannot store one, a mistyped password,
 * a cancelled biometric prompt, Forgot Password tapped from that prompt, and a cold restore that
 * ran out of attempts offline. Reporting those books a logout against a person who was never signed
 * in — the mistyped password most often, since it is the most common of them by far — and leaves the
 * rows with no `sessionEstablished` to divide into.
 *
 * Two things make it a real one, and between them they cover every path:
 *
 * 1. **Resource Tokens were live.** `authorized`, or a refresh replacing tokens it already held —
 *    `stale !== null`. That is the session ending under someone mid-use.
 * 2. **The Durable Root was destroyed.** `vaultCleared` is what the machine ordered, so a stored
 *    session ending counts even when none was live in this JS context: a Vault that cannot be read
 *    at launch clears it, and so does a refresh token the Authorization Server refused.
 *
 * Both are read off the states rather than off the event's cause. A cause says why something failed,
 * not whether a session existed to lose — `network` ends one when it interrupts a live session and
 * ends nothing when it interrupts a launch, and the two are the same cause.
 */
function endsASession(before: AuthState, after: Extract<AuthState, { tag: 'unauthenticated' }>): boolean {
  if (before.tag === 'unauthenticated') return false;
  if (after.vaultCleared === true) return true;
  if (before.tag === 'authorized') return true;
  return before.tag === 'refreshing' && before.stale !== null;
}

/**
 * Derives telemetry from transitions.
 *
 * This exists as a separate object from the effect that drives it for one reason: it is fed
 * entirely by `(before, after, event, commands)`, so every rule below is testable by calling
 * `record` with four values and asserting on a fake port. No TestBed, no Datadog, no timers.
 *
 * Reporting happens here rather than inside `transition()` so that `transition()` stays pure,
 * and here rather than at each call site so that it cannot be forgotten.
 *
 * **This must be constructed at bootstrap.** `auth.session_restore` is measured from
 * construction to the first settled state, so a lazily-created reporter would report a number
 * that measures injector timing rather than launch. `AuthEffects` injects it and `EffectsModule`
 * instantiates its effect classes during bootstrap, so that ordering holds without anyone
 * arranging it.
 */
@Injectable({ providedIn: 'root' })
export class AuthTelemetryReporter {
  private readonly telemetry = inject(AUTH_TELEMETRY);

  /**
   * Open duration vitals, so a start is never doubled and a stop is never unmatched.
   *
   * The value is the facets the *close* cannot work out for itself, taken when the region opens and
   * held here until it closes. `auth.login` spans several Grant states and only the first of them knows
   * which way in this was; `auth.refresh` closes into a state that no longer carries the trigger. See
   * {@link labelsOn}. `auth.biometric_sign_in` stores an empty pair.
   */
  private readonly open = new Map<AuthDuration, DurationLabels>();

  /** Session Restore is measured once per launch, not once per visit to `restoringSession`. */
  private settled = false;

  constructor() {
    this.start('auth.session_restore');
  }

  record(before: AuthState, after: AuthState, event: AuthEvent, commands: readonly Command[]): void {
    const from = describeState(before);
    const to = describeState(after);

    // `'cause' in event` and not a list of the failure events, for the same reason `stopFor` uses
    // it: a new event carrying a cause is labelled the moment it exists, with nobody having to
    // remember to add it here.
    //
    // The guard compares the states and not their labels. `describeState` is low-cardinality on
    // purpose, so nine transitions move the machine and leave the label alone — a retry and with it
    // the whole backoff sequence, a refused task, a wrong Challenge code, a re-issued Challenge,
    // `agentGone`, and a replay that failed. Every arm builds a fresh state and the
    // no-op arms return `state` itself, so identity is exact and the property this guard was added
    // for survives: a genuine no-op still emits nothing.
    if (before !== after) {
      this.telemetry.transition(from, to, event.type, 'cause' in event ? failureLabel(event.cause) : undefined);
    }

    // A no-op while a restore is in flight is single-flight working, not a lost event. Checked
    // against the observable result rather than the event alone, so that if one of these ever
    // becomes meaningful in `refreshing` this stops claiming it was coalesced.
    if (
      before.tag === 'refreshing' &&
      from === to &&
      commands.length === 0 &&
      (event.type === 'accessTokenRejected' || event.type === 'refreshRequested')
    ) {
      this.telemetry.coalescedRefresh(event.type);
    }

    if (after.tag === 'authorized' && before.tag !== 'authorized') {
      this.telemetry.sessionEstablished(originOf(before));
    }

    if (after.tag === 'unauthenticated' && endsASession(before, after)) {
      this.telemetry.signedOut(after.reason);
    }

    for (const command of commands) {
      if (command.do === 'reportIllegal') {
        this.telemetry.illegal(command.state, command.event);
      } else if (command.do === 'reportSecurityEvent') {
        this.telemetry.securityEvent(failureLabel(command.cause));
      }
    }

    this.measure(before, after, event, commands);
  }

  /** Report a Command handler that failed without producing an event. */
  commandFailed(command: Command): void {
    this.telemetry.commandFailed(command.do);
  }

  /**
   * Report what a failed authorization callback said about itself.
   *
   * A passthrough rather than something derived from a transition, and it has to be: the provider's
   * error is a property of the callback, not of the state change the machine makes because of it —
   * `transition()` routes it to `authorizationAbandoned` and keeps nothing else. Summarised at the
   * boundary that receives the callback, so the URL is never held anywhere.
   */
  authorizationError(summary: AuthorizationErrorSummary): void {
    this.telemetry.authorizationError(summary);
  }

  /**
   * The credential store misbehaved, in a way nothing else will report.
   *
   * Relayed rather than derived, like {@link authorizationError}: these are facts a handler learns and
   * the machine never hears, because none of them fails the operation that noticed.
   */
  credentialStoreFailed(operation: CredentialStoreOperation, detail: string): void {
    this.telemetry.credentialStoreFailed(operation, detail);
  }

  private measure(before: AuthState, after: AuthState, event: AuthEvent, commands: readonly Command[]): void {
    for (const region of REGIONS) {
      const was = region.holds(before);
      const is = region.holds(after);
      // The labels are read off `after`, which is the region's first state — see {@link labelsOn} for
      // why the close is too late to ask either of them.
      if (!was && is) this.start(region.name, labelsOn(after));
      else if (was && !is) this.stopFor(region.name, after, event);
    }

    // The prompt is bracketed by the Command that raises it and the event that answers it, rather
    // than by a state region. A region would work here — `replayingCredentials` opens and closes with
    // the prompt — and is still the wrong shape: what is being measured is how long a person spends in
    // front of the sensor, so it has to close on the answer even if the state it closes into changes
    // later for reasons of its own.
    //
    // It measures the prompt and the keychain read together, because on iOS they are one call: the
    // item is gated, so the OS prompt *is* the read. There is nothing finer to separate.
    //
    // The outcome is the prompt's own, not the sign-in's: credentials read successfully and then
    // refused by the Authorization Server is a successful read of a stale password, and `auth.login`
    // is where that failure belongs.
    if (commands.some(command => command.do === 'biometricSignIn')) {
      this.start('auth.biometric_sign_in');
    } else if (event.type === 'storedCredentialsRead') {
      this.stop('auth.biometric_sign_in', 'success');
    } else if (event.type === 'biometricSignInFailed') {
      this.stop('auth.biometric_sign_in', 'failure', failureLabel(event.cause));
    }

    // Session Restore spans `restoringSession` and `refreshing`, so it is bracketed by launch and
    // the first state a person can actually act in — which is the number they feel.
    //
    // `restoreDeferred` is one of those states: it is settled, it has a screen, and the launch is as
    // over as it is going to get. Left open, a launch that defers on a plane and completes forty
    // minutes later reports a forty-minute launch that succeeded, and one killed while deferred
    // reports nothing at all. It closes as the failure that deferred it — `stopFor` reads the event
    // first — and `settled` keeps the eventual restore from reopening the launch it already reported.
    if (
      !this.settled &&
      (after.tag === 'authorized' || after.tag === 'unauthenticated' || after.tag === 'restoreDeferred')
    ) {
      this.settled = true;
      this.stopFor('auth.session_restore', after, event);
    }
  }

  /**
   * Close a vital, taking the outcome and any cause from what ended it.
   *
   * The event is read before the state because the state can look like a success when it was not:
   * a refresh that exhausts its retries lands back on `authorized` with the tokens it started
   * with, and a failed Company Switch does the same. Both are failures the person waited through,
   * and pooling them with clean refreshes would flatter the refresh failure rate.
   */
  private stopFor(name: AuthDuration, after: AuthState, event: AuthEvent): void {
    if ('cause' in event) {
      this.stop(name, 'failure', failureLabel(event.cause));
      return;
    }
    if (after.tag !== 'unauthenticated') {
      this.stop(name, 'success');
      return;
    }
    const error = after.flow.kind === 'idle' ? after.flow.error : undefined;
    this.stop(name, 'failure', error && failureLabel(error));
  }

  private start(name: AuthDuration, labels: DurationLabels = {}): void {
    if (this.open.has(name)) return;
    this.open.set(name, labels);
    this.telemetry.startDuration(name);
  }

  /** The labels come off the open vital, because only its start could know them. */
  private stop(name: AuthDuration, outcome: DurationOutcome, failure?: AuthFailureLabel): void {
    const labels = this.open.get(name);
    if (!labels) return;
    this.open.delete(name);
    this.telemetry.stopDuration(name, outcome, failure, labels);
  }
}
